// Account-First Phase 2 — extracted body of the LA review surface so the
// same controls (preview, AI distillation, scan-override, return-with-
// comments, file-into-folder, approve-and-file) can be embedded inline
// on the package detail page reached via the account hub. The standalone
// /collection/staging/:id page renders <PageHeader> above this panel and
// supplies onComplete to navigate back to the queue; inline callers
// supply onComplete to refresh their own list.

import { useState, useMemo, useEffect, useId, useRef } from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Download,
  Clock,
  RotateCcw,
  Send,
  ShieldAlert,
  ShieldCheck,
  ShieldHalf,
  ShieldQuestion,
  Users,
} from "lucide-react";
import {
  Badge,
  Button,
  FormField,
  Input,
  Textarea,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { FolderPicker } from "@/components/dms/FolderPicker";
import { MarkdownReader } from "@/components/reports/v32/MarkdownReader";
import { DocumentComparisonPanel } from "@/components/review/DocumentPreview";
import { ScanRecoveryControls } from "@/components/review/ScanRecoveryControls";
import {
  collectionStagingApi,
  type StagedSubmissionDetail,
  type StagedDestinationOption,
  type ScanStatus,
  type BrokerApplicationImportBlockReason,
} from "@/api/collectionStaging";
import { ApiError } from "@/api/client";
import { collectionStagingKeys } from "@/api/collectionStagingKeys";
import { collectionReviewQueueKeys } from "@/api/collectionReviewQueueKeys";
import { isSessionExpired, retryUnlessSessionExpired } from "@/api/polling";
import { collectionPackagesApi } from "@/api/collectionPackages";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { collectionPackagesKeys } from "@/hooks/collection/collectionPackagesKeys";
import { portalAccountsKeys } from "@/hooks/portalAccounts/portalAccountsKeys";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";
import { OwnerPicker, type OwnerSelection } from "@/components/Collection/OwnerPicker";
import { PackageAssigneesEditor } from "@/components/Collection/PackageAssigneesEditor";
import { ApprovalChainTimeline } from "@/components/Collection/ApprovalChain/ApprovalChainTimeline";
import { useInternalUserLabels, fallbackUserLabel } from "@/api/internalUsers";
import { useAuth } from "@/hooks/useAuth";
import { formatDateTimeInTz, useDateFormatter } from "@/utils/datetime";
import { parseFormSchema } from "@/lib/formSchema";
import { PortalFormRenderer, type PortalFormData } from "@/portal/components/PortalFormRenderer";
import {
  ITEM_STATUS,
  packageProgressLine,
  reviewRowStatus,
} from "@/components/Collection/collectionStatusLabels";
import {
  UNRESOLVED_STAFF_NAME,
  reviewEventActor,
  reviewEventText,
  reviewHistoryOf,
  staffActorIds,
} from "@/components/Collection/reviewHistory";

interface ScanBadgeProps {
  status: ScanStatus;
  // FL-12 — when set, the Clean status came from a manual LA override
  // (OverrideScanAsync) rather than the AV scanner. Render the amber
  // override badge with a who/when/why tooltip so the reviewer can tell.
  overriddenAt?: string | null;
  overriddenBy?: string | null;
  overrideReason?: string | null;
  tz: string;
}

function ScanBadge({
  status,
  overriddenAt,
  overriddenBy,
  overrideReason,
  tz,
}: ScanBadgeProps) {
  if (status === "Clean" && overriddenAt) {
    const parts = [
      `Scan manually overridden${overriddenBy ? ` by ${overriddenBy}` : ""}`,
      `on ${formatDateTimeInTz(overriddenAt, tz)}.`,
    ];
    if (overrideReason) parts.push(`Reason: ${overrideReason}`);
    return (
      <Badge status="review" title={parts.join(" ")}>
        <ShieldHalf size={12} className="mr-1 inline" /> Scan overridden
      </Badge>
    );
  }
  switch (status) {
    case "Clean":
      return (
        <Badge status="finalized">
          <ShieldCheck size={12} className="mr-1 inline" /> Scan clean
        </Badge>
      );
    case "Infected":
      return (
        <Badge status="archived">
          <ShieldAlert size={12} className="mr-1 inline" /> Quarantined
        </Badge>
      );
    case "Error":
      return <Badge status="info">Scan error</Badge>;
    case "Pending":
    default:
      return (
        <Badge status="info">
          <ShieldQuestion size={12} className="mr-1 inline" /> Scanning…
        </Badge>
      );
  }
}

function parseSavedFormData(blob: string | null | undefined): PortalFormData {
  if (!blob) return {};
  try {
    const parsed = JSON.parse(blob) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as PortalFormData
      : {};
  } catch {
    return {};
  }
}

export type StagedSubmissionReviewOutcome =
  | "approved"
  | "returned"
  | "overridden";

/** "Approve and Return" / "Approve, Return and File". */
function joinActions(actions: string[]): string {
  return actions.length <= 1
    ? actions.join("")
    : `${actions.slice(0, -1).join(", ")} and ${actions[actions.length - 1]}`;
}

interface StagedSubmissionReviewPanelProps {
  /** StagedSubmission id to review. */
  id: string;
  /**
   * Called after a terminal review action (approve, return). Inline
   * callers use this to invalidate their per-package list; the standalone
   * page uses it to navigate back to the queue. Not invoked on the
   * non-terminal scan-override.
   */
  onComplete?: (outcome: StagedSubmissionReviewOutcome) => void;
  /**
   * §2.4 entity-surface mounts — hide the queue-management actions
   * (Manage Assignees, Change owner) that belong on /collection/staging,
   * not on a per-entity Review & requests tab. Default falsy: the full
   * queue renders byte-unchanged.
   */
  hideQueueActions?: boolean;
}

/**
 * Loan-Administrator review surface: submission preview + AI distillation,
 * return-with-comments thread, file-into-folder picker, approve-and-file.
 * Extracted from <CollectionStagingReviewPage> (PR 7) so the package
 * detail page reached via the account hub can embed the same controls
 * inline (Account-First Phase 2).
 */
/** Pull the field-tagged broker-import block reasons out of the ApiResponse
 *  `details` array on a 400 (mirror of the deal-path parseImportBlockReasons). */
export function parseBrokerImportBlockReasons(err: unknown): BrokerApplicationImportBlockReason[] {
  const details = err instanceof ApiError ? err.details : undefined;
  if (!Array.isArray(details)) return [];
  return details.filter(
    (r): r is BrokerApplicationImportBlockReason =>
      typeof r === "object" && r !== null && typeof (r as { message?: unknown }).message === "string",
  );
}

/** Approve-and-file lands a Document in a DMS folder whose id isn't known
 *  here, so the DMS folder + document list prefixes are invalidated broadly
 *  on purpose — those queries are inactive on this page, so they're only
 *  stale-marked (no refetch storm). Exported for the invalidation-harness
 *  unit test. */
export function invalidateAfterStagingFile(qc: QueryClient, id: string) {
  invalidateAfterReviewDecision(qc, id);
  void qc.invalidateQueries({ queryKey: ["dms", "folderDetail"] });
  void qc.invalidateQueries({ queryKey: ["dms", "folderDocs"] });
  void qc.invalidateQueries({ queryKey: ["documents"] });
}

/** Every read a review decision (approve, file, return, reopen) changes: the
 *  detail itself, every staging list and account summary (the
 *  "collection-staging" prefix), the portal account's package roll-ups and
 *  per-package item lists ("portal-account-packages"), the package detail page,
 *  the portal-account summary header, and the global entity Review Queue. */
export function invalidateAfterReviewDecision(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: collectionStagingKeys.detail(id) });
  void qc.invalidateQueries({ queryKey: collectionStagingKeys.all });
  void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.accountPackagesPrefix });
  void qc.invalidateQueries({ queryKey: collectionPackagesKeys.packageDetailPrefix });
  void qc.invalidateQueries({ queryKey: collectionPackagesKeys.portalAccountSummaryPrefix });
  void qc.invalidateQueries({ queryKey: collectionReviewQueueKeys.all });
}

export function StagedSubmissionReviewPanel({
  id,
  onComplete,
  hideQueueActions = false,
}: StagedSubmissionReviewPanelProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const { date, dateTime, tz } = useDateFormatter();
  const canOverrideScan = hasPermission("Collection.Staging.OverrideScan");
  // Regenerate/Refresh are benign recovery actions — available to any reviewer
  // who can work this queue, not just the elevated scan-override role.
  const canReview = hasPermission("Collection.Staging.ReviewAndFile");
  // PROB-24b — only package managers (Loan-Administrator+) edit assignees.
  const canManageAssignees = hasPermission("Collection.ManagePackages");

  const [folderId, setFolderId] = useState<string | null>(null);
  // When a filed AI distillation accompanies the upload, the reviewer can
  // keep (default) or skip the auto-link to the source document. Reset to
  // on whenever the panel switches to a different submission.
  const [linkAiDistillation, setLinkAiDistillation] = useState(true);
  useEffect(() => {
    setLinkAiDistillation(true);
  }, [id]);
  // Review-surface consolidation §2/§4 — the reviewer must open the file at
  // least once this session before Approve / Request Changes unlock. Tracks
  // the first time the inline viewer rendered or the file was opened in a new
  // tab; reset whenever the panel switches to a different submission.
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  const markOpened = () => setOpenedAt((prev) => prev ?? Date.now());
  // Review loop — "Reopen for review" on a Returned submission: an inline
  // note (required) + confirm. Reset with the rest of the per-submission state.
  const [reopenMode, setReopenMode] = useState(false);
  const [reopenNote, setReopenNote] = useState("");
  useEffect(() => {
    setOpenedAt(null);
    setReopenMode(false);
    setReopenNote("");
  }, [id]);
  const returnedBannerId = useId();
  const reopenReasonId = useId();
  // PROB-C — confirm before firing an on-demand reminder email.
  const [remindConfirmOpen, setRemindConfirmOpen] = useState(false);
  // PROB-E — in-app archive confirmation (replaces window.confirm).
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [returnNote, setReturnNote] = useState("");
  const [returnMode, setReturnMode] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  // PR 3 §10.8 — retarget-owner panel state. PROB-16: the raw owner-type
  // + GUID inputs were replaced by the shared OwnerPicker, so the panel
  // holds a single OwnerSelection (resolved entity) instead of the two
  // free-text strings.
  const [retargetMode, setRetargetMode] = useState(false);
  const [retargetOwner, setRetargetOwner] = useState<OwnerSelection | null>(null);
  // PROB-24b — Manage Assignees inline editor (many-to-many). The panel is a
  // live editor: each add / remove fires the controller endpoint immediately;
  // there is no Save button. The InternalUserPicker renders current assignees
  // as removable chips.
  const [assignMode, setAssignMode] = useState(false);
  // Prompt 9-iii — the active quick-pick anchor whose folder tree is shown
  // in the inline tree. Defaults to the package owner; switches when staff
  // clicks an attached-record chip or a free-search result whose owner
  // metadata is known.
  const [anchor, setAnchor] = useState<{
    entityType: string;
    entityId: string;
  } | null>(null);

  const detail = useQuery({
    queryKey: ["staging-detail", id],
    queryFn: () => collectionStagingApi.get(id),
    enabled: Boolean(id),
    // Poll while the assessment is still generating so the panel resolves to
    // Completed/Failed on its own (and the server watchdog is tripped). The query
    // otherwise inherits the app's 5-min staleTime and never re-reads, so a
    // finished/stuck run keeps showing "Generating…" indefinitely.
    refetchInterval: (query) => {
      // Session expired — stop polling so an unattended tab doesn't 401 forever.
      if (isSessionExpired(query.state.error)) return false;
      const d = query.state.data;
      return d?.assessmentStatus === "Pending" && d?.assessmentStartedAt ? 5000 : false;
    },
    retry: retryUnlessSessionExpired,
  });

  const submission: StagedSubmissionDetail | undefined = detail.data;

  // Reconcile an unresolved scan on open. The detail query inherits the app's
  // 5-min staleTime (and never polls), so re-opening the drawer can show a
  // Pending/Error scan that has since resolved server-side — a webhook verdict,
  // or the server's heal-on-read where scanning isn't wired. When the loaded
  // status is non-terminal, refetch once so the reviewer sees the current
  // result without having to click "Refresh scan". Keyed by submission id so it
  // fires once per open and can't loop when the scan is genuinely still Pending.
  const reconciledScanRef = useRef<string | null>(null);
  const refetchDetail = detail.refetch;
  const loadedSubmissionId = submission?.id;
  const loadedScanStatus = submission?.scanStatus;
  useEffect(() => {
    if (!loadedSubmissionId || reconciledScanRef.current === loadedSubmissionId) return;
    if (loadedScanStatus === "Pending" || loadedScanStatus === "Error") {
      reconciledScanRef.current = loadedSubmissionId;
      void refetchDetail();
    }
  }, [loadedSubmissionId, loadedScanStatus, refetchDetail]);

  const reviewFormSchema = useMemo(
    () => parseFormSchema(submission?.formSchemaJson),
    [submission?.formSchemaJson],
  );
  const [reviewFormData, setReviewFormData] = useState<PortalFormData>({});
  const [reviewFormDirty, setReviewFormDirty] = useState(false);
  // Field-tagged reasons the last broker import was blocked (unrecognized State,
  // invalid Phone). Cleared on a fresh import attempt and when the panel switches.
  const [importBlockReasons, setImportBlockReasons] = useState<BrokerApplicationImportBlockReason[]>([]);
  useEffect(() => {
    setReviewFormData(parseSavedFormData(submission?.savedFormDataJson));
    setReviewFormDirty(false);
    setImportBlockReasons([]);
  }, [submission?.id, submission?.savedFormDataJson]);

  // PROB-24a — resolve assignee ids to names for the header chip. Explicit
  // assignees lead; when there are none we fall back to the DealRole
  // effective assignee so the header still names someone.
  const headerAssigneeIds = useMemo(() => {
    const ids = [...(submission?.assigneeUserIds ?? [])];
    if (ids.length === 0 && submission?.effectiveAssignedToUserId) {
      ids.push(submission.effectiveAssignedToUserId);
    }
    return ids;
  }, [submission?.assigneeUserIds, submission?.effectiveAssignedToUserId]);
  const headerLabels = useInternalUserLabels(headerAssigneeIds);

  // Review loop — Review history (newest first) and the Returned banner name
  // staff by resolving their ids; never print an id.
  const reviewEvents = useMemo(
    () => (submission ? reviewHistoryOf(submission) : []),
    [submission],
  );
  const reviewActorIds = useMemo(() => {
    const ids = staffActorIds(reviewEvents);
    if (submission?.returnedByUserId) ids.push(submission.returnedByUserId);
    return ids;
  }, [reviewEvents, submission?.returnedByUserId]);
  const reviewActorLabels = useInternalUserLabels(reviewActorIds);
  const staffName = (userId: string) => reviewActorLabels.lookup(userId)?.displayName;

  // Smart-default destination — fetched once we know the owner. The query
  // also auto-provisions the folder tree if it doesn't exist yet, so the
  // folder picker can render straight after.
  const suggestion = useQuery({
    queryKey: ["staging-suggestion", id],
    queryFn: () => collectionStagingApi.suggestedFolder(id),
    enabled: Boolean(submission),
  });

  // Pre-select the suggested folder once it arrives, but only if the LA
  // hasn't already picked one.
  useEffect(() => {
    if (folderId == null && suggestion.data?.suggestedFolderId) {
      setFolderId(suggestion.data.suggestedFolderId);
    }
  }, [folderId, suggestion.data]);

  // Prompt 9-iii — quick-pick destinations: package owner + every record
  // the recipient's portal account is attached to (Person, Business,
  // Deal, …). Each anchor's tree is browsable inline; the search box
  // below it spans every folder in the system.
  const destinations = useQuery({
    queryKey: ["staging-destinations", id],
    queryFn: () => collectionStagingApi.destinationOptions(id),
    enabled: Boolean(submission),
  });

  // Seed the active anchor from the package owner once the submission
  // loads. Staff can switch by clicking another quick-pick chip.
  useEffect(() => {
    if (anchor == null && submission) {
      setAnchor({
        entityType: submission.ownerEntityType,
        entityId: submission.ownerEntityId,
      });
    }
  }, [anchor, submission]);

  const downloadUrl = useQuery({
    queryKey: ["staging-download", id],
    queryFn: () => collectionStagingApi.downloadUrl(id),
    enabled: Boolean(submission),
  });

  const assessmentPreviewUrl = useQuery({
    queryKey: ["staging-assessment-preview", id, "pdf"],
    queryFn: async () => (await collectionStagingApi.assessmentDownloadUrl(id, "pdf")) ?? null,
    enabled: Boolean(submission?.hasAssessmentPdf),
  });

  const saveFormData = useMutation({
    mutationFn: () => collectionStagingApi.updateFormData(id, JSON.stringify(reviewFormData)),
    onSuccess: (updated) => {
      toast({ tone: "success", message: "Reviewed form answers saved." });
      setReviewFormDirty(false);
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      qc.setQueryData(["staging-detail", id], updated);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const importBrokerApplication = useMutation({
    mutationFn: async () => {
      if (reviewFormDirty) {
        await collectionStagingApi.updateFormData(id, JSON.stringify(reviewFormData));
      }
      return collectionStagingApi.importBrokerApplication(id);
    },
    onMutate: () => setImportBlockReasons([]),
    onSuccess: () => {
      toast({ tone: "success", message: "Broker application imported." });
      setReviewFormDirty(false);
      setImportBlockReasons([]);
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      void qc.invalidateQueries({ queryKey: brokerKeys.all });
      if (submission?.portalUserId) {
        void qc.invalidateQueries({ queryKey: ["portal-account", submission.portalUserId] });
      } else {
        void qc.invalidateQueries({ queryKey: ["portal-accounts"] });
      }
    },
    onError: (e: Error) => {
      // Prefer field-tagged reasons (inline "fix this field"); fall back to a toast.
      const reasons = parseBrokerImportBlockReasons(e);
      setImportBlockReasons(reasons);
      if (reasons.length === 0) toast({ tone: "error", message: e.message });
    },
  });

  const approve = useMutation({
    mutationFn: () => collectionStagingApi.approve(id),
    onSuccess: (updated) => {
      toast({ tone: "success", message: "Submission approved." });
      qc.setQueryData(["staging-detail", id], updated);
      invalidateAfterReviewDecision(qc, id);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const file = useMutation({
    mutationFn: () => collectionStagingApi.file(id, folderId!, linkAiDistillation),
    onSuccess: (updated) => {
      toast({ tone: "success", message: "Submission filed." });
      qc.setQueryData(["staging-detail", id], updated);
      invalidateAfterStagingFile(qc, id);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const returnMut = useMutation({
    mutationFn: () => collectionStagingApi.return(id, returnNote.trim()),
    onSuccess: () => {
      toast({
        tone: "success",
        message: "Returned to recipient with comments.",
      });
      // Review history renders off the detail payload — stale-mark it so a
      // revisit shows the new return.
      invalidateAfterReviewDecision(qc, id);
      onComplete?.("returned");
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // Review loop — put a Returned submission back into review. No email goes
  // to the recipient. The drawer stays open on the refreshed row (no
  // onComplete); a 409 means the row moved underneath us, so re-read it.
  const reopenMut = useMutation({
    mutationFn: () => collectionStagingApi.reopen(id, reopenNote.trim()),
    onSuccess: (updated) => {
      toast({
        tone: "success",
        message: "Reopened for review. No email was sent to the recipient.",
      });
      setReopenMode(false);
      setReopenNote("");
      if (updated) qc.setQueryData(collectionStagingKeys.detail(id), updated);
      invalidateAfterReviewDecision(qc, id);
    },
    onError: (e: Error) => {
      toast({ tone: "error", message: e.message });
      if (e instanceof ApiError && e.status === 409) {
        void qc.invalidateQueries({ queryKey: collectionStagingKeys.detail(id) });
      }
    },
  });

  const overrideMut = useMutation({
    mutationFn: () =>
      collectionStagingApi.overrideScan(id, overrideReason.trim()),
    onSuccess: () => {
      toast({ tone: "success", message: "Scan status overridden." });
      setOverrideMode(false);
      setOverrideReason("");
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      void qc.invalidateQueries({ queryKey: ["collection-staging"] });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // Staging recovery — manual recovery for stuck async jobs. Both reflect the
  // server's actual state after the action (no optimistic UI); toasts mirror
  // the real outcome.
  const regenerateMut = useMutation({
    mutationFn: () => collectionStagingApi.regenerateAssessment(id),
    onSuccess: () => {
      toast({ tone: "success", message: "Assessment regeneration started." });
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      void qc.invalidateQueries({ queryKey: ["collection-staging"] });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const refreshScanMut = useMutation({
    mutationFn: () => collectionStagingApi.refreshScan(id),
    onSuccess: () => {
      toast({ tone: "success", message: "Scan refresh started." });
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      void qc.invalidateQueries({ queryKey: ["collection-staging"] });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // PR 3 §10.3 — on-demand reminder.
  const remindMut = useMutation({
    mutationFn: () => collectionPackagesApi.remind(submission!.packageId),
    onSuccess: () => {
      toast({ tone: "success", message: `Reminder sent to ${submission!.recipientName}.` });
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      void qc.invalidateQueries({ queryKey: ["collection-staging"] });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // PR 3 §10.5 — archive the parent package; the staging detail keeps
  // rendering (audit history) but the queue hides it on next refresh.
  const archiveMut = useMutation({
    mutationFn: () => collectionPackagesApi.archive(submission!.packageId),
    onSuccess: () => {
      toast({ tone: "success", message: "Package archived." });
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      void qc.invalidateQueries({ queryKey: ["collection-staging"] });
      void qc.invalidateQueries({ queryKey: ["collection-package"] });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // WS-F — archive THIS requested item (the one under review). A submission
  // exists, so the item is touched by definition → Archive only, never Delete.
  // refreshAfterAssigneeChange() covers the staging-detail / collection-staging
  // / collection-package reads (its existing inline keys); the factory keys
  // below refresh the account's package roll-ups (counts/progress on the Review
  // Queue tab + account detail). Server-gated by CanArchiveOrDeleteAsync.
  const archiveItemMut = useMutation({
    mutationFn: () =>
      collectionPackagesApi.archiveItem(submission!.packageId, submission!.collectionItemId),
    onSuccess: () => {
      toast({ tone: "success", message: "Item archived." });
      refreshAfterAssigneeChange();
      void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.accountPackagesPrefix });
      void qc.invalidateQueries({ queryKey: collectionPackagesKeys.portalAccountSummaryPrefix });
      void qc.invalidateQueries({ queryKey: portalAccountsKeys.all() });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // PR 3 §10.8 — staff fix-up when the package was attached to the
  // wrong owner record.
  const retargetMut = useMutation({
    mutationFn: () => {
      if (!retargetOwner) {
        throw new Error("Pick an owner before retargeting.");
      }
      return collectionPackagesApi.retargetOwner(
        submission!.packageId,
        retargetOwner.ownerEntityType,
        retargetOwner.ownerEntityId,
      );
    },
    onSuccess: () => {
      toast({ tone: "success", message: "Package owner retargeted." });
      setRetargetMode(false);
      setRetargetOwner(null);
      void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
      void qc.invalidateQueries({ queryKey: ["staging-destinations", id] });
      void qc.invalidateQueries({ queryKey: ["staging-suggestion", id] });
      void qc.invalidateQueries({ queryKey: ["collection-staging"] });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  // PROB-G — the assignee add/remove diff now lives in the shared
  // <PackageAssigneesEditor> (reused by the queue-row quick-action modal).
  // The panel keeps only this refresh hook so the context strip + queue
  // cache update after an inline edit.
  function refreshAfterAssigneeChange() {
    void qc.invalidateQueries({ queryKey: ["staging-detail", id] });
    void qc.invalidateQueries({ queryKey: ["collection-staging"] });
    void qc.invalidateQueries({ queryKey: ["collection-package"] });
  }

  if (!submission) {
    return (
      <div className="flex flex-col gap-space-2">
        <p className="text-small text-gray-mid">Loading submission…</p>
        {detail.isError && (
          <p className="text-small text-red-600">
            {detail.error instanceof Error
              ? detail.error.message
              : "Submission not found."}
          </p>
        )}
      </div>
    );
  }

  const fileOpened = openedAt != null;

  const isAwaiting = submission.reviewStatus === "AwaitingReview";
  const isApproved = submission.reviewStatus === "Approved";
  const isReturned = submission.reviewStatus === "Returned";
  // D9 — Report Request rows get the plain-English labels only: no Returned
  // banner and no Reopen (they are reviewed in the loan's Report Requests panel).
  const showReturnedBanner = isReturned && !submission.isReportRequest;
  const isSuperseded = submission.reviewStatus === "Superseded";
  const isFiled = submission.filedDocumentId != null;
  // Edit-after-lock: reviewed answers stay editable through Approved so a
  // reviewer can correct a value that blocked the broker import without
  // un-approving. Returned / Superseded are read-only. Mirrors the backend
  // UpdateFormDataAsync allowance.
  const answersEditable =
    submission.reviewStatus === "AwaitingReview" || submission.reviewStatus === "Approved";
  const canApprove =
    submission.reviewStatus === "AwaitingReview" &&
    fileOpened;
  const canFile =
    submission.scanStatus === "Clean" &&
    !isFiled &&
    (submission.reviewStatus === "AwaitingReview" || submission.reviewStatus === "Approved") &&
    fileOpened &&
    folderId != null;

  // Review loop — say why a review action is unavailable, beside the buttons,
  // instead of a hover-only hint. A Returned row's reason is its banner.
  const actionReasons: string[] = [];
  if (isSuperseded) {
    actionReasons.push(
      "A newer submission replaced this copy, so Approve, File and Return are unavailable here.",
    );
  } else if (!isReturned) {
    const fileableOnceOpened =
      submission.scanStatus === "Clean" && !isFiled && (isAwaiting || isApproved) && folderId != null;
    const unlockedByOpening = fileOpened
      ? []
      : [...(isAwaiting ? ["Approve", "Return"] : []), ...(fileableOnceOpened ? ["File"] : [])];
    if (unlockedByOpening.length > 0) {
      actionReasons.push(`Open the file to enable ${joinActions(unlockedByOpening)}.`);
    }
    if (!isFiled && (isAwaiting || isApproved)) {
      if (submission.scanStatus !== "Clean") {
        actionReasons.push("File is available once the malware scan is clean.");
      } else if (folderId == null) {
        actionReasons.push("Pick a folder to enable File.");
      }
    }
  }

  // Review loop — the drawer leads with the ITEM's status (what the request as
  // a whole is doing); the package roll-up is a clearly-labelled second line.
  const leadStatus = submission.itemStatus ? ITEM_STATUS[submission.itemStatus] ?? null : null;
  const fallbackStatus = reviewRowStatus(submission);
  const packageLine = packageProgressLine(
    submission.packageStatus,
    submission.packageItemsApproved,
    submission.packageItemsTotal,
  );
  const returnedByName =
    (submission.returnedByUserId ? staffName(submission.returnedByUserId)?.trim() : null) ||
    UNRESOLVED_STAFF_NAME;

  // The auto-link toggle only matters when a filed AI distillation exists —
  // i.e. a branded .pdf/.docx that becomes a real Document on approve. The
  // markdown alone (failed render) files no companion, so there is nothing
  // to link and the toggle stays out of the DOM.
  const hasAiDistillation =
    submission.hasAssessmentPdf || submission.hasAssessmentDocx;

  // Malware-scan preview gate. The inline viewer auto-renders the submitted
  // file in an <iframe>/<img>, which would execute a malicious payload before
  // the scan has reached a verdict — defeating the scan's purpose. So the
  // inline preview is withheld until the scan resolves Clean (a manual
  // scan-override also sets the status to Clean, which unblocks it). Download /
  // Open stay available throughout so staff can still review the file manually
  // in a safe context.
  const scanPreviewBlockedReason =
    submission.scanStatus === "Clean"
      ? null
      : submission.scanStatus === "Infected"
        ? "This file was flagged as infected by the malware scan, so the inline preview is disabled. Download it only to inspect in an isolated environment."
        : submission.scanStatus === "Error"
          ? "The malware scan could not complete, so the inline preview is disabled. Refresh the scan, or download the file to review it manually."
          : "The malware scan is still running, so the inline preview is disabled until it finishes. You can download the file to review it manually in the meantime.";
  const scanPreviewAllowed = scanPreviewBlockedReason === null;

  return (
    <div className="flex flex-col gap-space-4">
      <section className="flex flex-col gap-space-1" data-testid="review-status-header">
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="text-small font-semibold text-onyx">
            {leadStatus ? "Item:" : "Submission:"}
          </span>{" "}
          <Badge status={(leadStatus ?? fallbackStatus).tone}>
            {(leadStatus ?? fallbackStatus).label}
          </Badge>
        </div>
        {packageLine && (
          <p className="m-0 text-caption text-gray-mid" data-testid="review-package-line">
            {packageLine}
          </p>
        )}
      </section>

      {/* Approval chains (D12) — the item's routing timeline and staff
          actions; renders nothing for an item without a chain. */}
      <ApprovalChainTimeline
        itemId={submission.collectionItemId}
        packageId={submission.packageId}
        hasPermission={hasPermission}
        revise={{
          templateId: submission.templateId,
          dealId: submission.ownerEntityType === "Deal" ? submission.ownerEntityId : null,
          recipientName: submission.recipientName,
          savedFormDataJson: submission.savedFormDataJson,
        }}
      />

      {/* Review loop — a Returned submission is waiting on the recipient. The
          banner is the reason Approve / File / Return are unavailable, and
          carries the one way back into review without them: Reopen. */}
      {showReturnedBanner && (
        <section
          id={returnedBannerId}
          data-testid="returned-banner"
          className="rounded-md border border-status-error bg-status-error/5 p-space-3 flex flex-col gap-space-3"
        >
          <p className="m-0 text-small text-onyx">
            Returned by {returnedByName}
            {submission.returnedAt ? ` on ${date(submission.returnedAt)}` : ""}. Waiting on the
            recipient — Approve, File and Return are unavailable until they respond or you
            reopen it.
          </p>
          {submission.canReopen ? (
            reopenMode ? (
              <div className="flex flex-col gap-space-2">
                <FormField
                  label="Why are you reopening it?"
                  required
                  helperText="Recorded in Review history. The recipient is not emailed."
                >
                  <Textarea
                    rows={3}
                    maxLength={4000}
                    value={reopenNote}
                    onChange={(e) => setReopenNote(e.target.value)}
                  />
                </FormField>
                <div className="flex items-center justify-end gap-space-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setReopenMode(false);
                      setReopenNote("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={reopenNote.trim().length === 0 || reopenMut.isPending}
                    loading={reopenMut.isPending}
                    onClick={() => reopenMut.mutate()}
                  >
                    Confirm reopen
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <Button type="button" variant="secondary" onClick={() => setReopenMode(true)}>
                  <RotateCcw size={14} className="mr-1 inline" />
                  Reopen for review
                </Button>
              </div>
            )
          ) : (
            <div className="flex flex-wrap items-center gap-space-2">
              <Button type="button" variant="secondary" disabled aria-describedby={reopenReasonId}>
                <RotateCcw size={14} className="mr-1 inline" />
                Reopen for review
              </Button>
              <span id={reopenReasonId} className="text-caption text-gray-mid">
                {submission.reopenBlockedReason?.trim() ||
                  "Reopen isn't available for this submission."}
              </span>
            </div>
          )}
        </section>
      )}

      {/* PR 3 §10.1 / §10.2 / §10.5 — context strip: effective assignee +
          days-since-sent, plus per-package actions (remind, archive, retarget
          owner, reassign). The parent package state is the status header's
          "Package:" line above. */}
      <section className="rounded-md border border-gray-light p-space-3 flex flex-wrap items-center gap-space-4 bg-paper-warm">
        <div className="flex flex-col">
          <span className="text-caption uppercase text-gray-mid">Assigned to</span>
          {(() => {
            // PROB-24a — resolved names, never a raw GUID; no "(explicit)"
            // annotation. Explicit assignees render comma-separated (first 3
            // + "+N more"); a DealRole fallback shows the role code.
            const explicitIds = submission.assigneeUserIds ?? [];
            if (explicitIds.length > 0) {
              const names = explicitIds.map(
                (uid) => headerLabels.lookup(uid)?.displayName ?? fallbackUserLabel(uid),
              );
              const shown = names.slice(0, 3).join(", ");
              const extra = names.length > 3 ? ` +${names.length - 3} more` : "";
              return (
                <span className="text-small text-onyx" title={names.join(", ")}>
                  <Users size={12} className="mr-1 inline" />
                  {shown}
                  {extra}
                </span>
              );
            }
            if (submission.effectiveAssignedToUserId) {
              const name =
                headerLabels.lookup(submission.effectiveAssignedToUserId)?.displayName ??
                fallbackUserLabel(submission.effectiveAssignedToUserId);
              const roleSuffix =
                submission.effectiveAssignedToSource === "DealRole" &&
                submission.effectiveAssignedToRoleCode
                  ? ` (${submission.effectiveAssignedToRoleCode})`
                  : "";
              return (
                <span className="text-small text-onyx">
                  <Users size={12} className="mr-1 inline" />
                  {name}
                  {roleSuffix}
                </span>
              );
            }
            return <span className="text-small text-gray-mid italic">Unassigned</span>;
          })()}
        </div>
        <div className="flex flex-col">
          <span className="text-caption uppercase text-gray-mid">Age</span>
          {submission.daysSinceSent != null ? (
            <span className={["text-small", submission.isStale ? "text-red-600 font-semibold" : "text-onyx"].join(" ")}>
              <Clock size={12} className="mr-1 inline" />
              {submission.daysSinceSent} day{submission.daysSinceSent === 1 ? "" : "s"} since sent
              {submission.isStale ? " — stale" : ""}
            </span>
          ) : (
            <span className="text-small text-gray-mid italic">Draft</span>
          )}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-space-2">
          {(() => {
            const remindBlockedReason =
              submission.packageStatus === "Draft"
                ? "Package not yet sent"
                : submission.packageStatus === "Completed"
                  ? "Package already completed"
                  : submission.packageStatus === "Cancelled" || submission.packageStatus === "Archived"
                    ? `Package ${submission.packageStatus.toLowerCase()}`
                    : null;
            return (
              <Button
                type="button"
                variant="secondary"
                disabled={remindMut.isPending || remindBlockedReason !== null}
                onClick={() => setRemindConfirmOpen(true)}
                title={remindBlockedReason ?? "Send a reminder email to the recipient"}
              >
                <Send size={12} className="mr-1 inline" /> Send reminder
              </Button>
            );
          })()}
          {!hideQueueActions && canManageAssignees && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAssignMode((v) => !v)}
            >
              <Users size={12} className="mr-1 inline" />
              {assignMode ? "Cancel manage assignees" : "Manage Assignees"}
            </Button>
          )}
          {!hideQueueActions && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRetargetMode((v) => !v)}
            >
              {retargetMode ? "Cancel owner change" : "Change owner"}
            </Button>
          )}
          {/* WS-F — archive THIS requested item (recoverable). Alongside the
              whole-package Archive; the item is touched (a submission exists) so
              soft-Delete is never offered here. */}
          <Button
            type="button"
            variant="secondary"
            disabled={archiveItemMut.isPending}
            loading={archiveItemMut.isPending}
            onClick={() => archiveItemMut.mutate()}
            title="Archive this requested item"
          >
            <Archive size={12} className="mr-1 inline" /> Archive item
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={submission.packageStatus === "Archived" || archiveMut.isPending}
            onClick={() => setArchiveConfirmOpen(true)}
          >
            <Archive size={12} className="mr-1 inline" /> Archive package
          </Button>
        </div>
      </section>

      {/* PROB-24b — Manage Assignees inline editor. Many-to-many: the picker
          renders current assignees as removable chips; each add / remove
          writes immediately (no Save button). Staff search by name / email /
          role code and never see or paste a raw user id. */}
      {!hideQueueActions && assignMode && canManageAssignees && (
        <section className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-2">
          <h3 className="text-small font-semibold text-onyx">Manage assignees</h3>
          <PackageAssigneesEditor
            packageId={submission.packageId}
            onChanged={refreshAfterAssigneeChange}
          />
          <div className="flex items-center justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAssignMode(false)}
            >
              Done
            </Button>
          </div>
        </section>
      )}

      {/* PR 3 §10.8 — retarget owner inline editor. PROB-16: the raw
          owner-type + GUID inputs were replaced by the shared
          OwnerPicker so staff can search by deal #, person/business
          name, etc. without ever pasting a GUID. */}
      {!hideQueueActions && retargetMode && (
        <section className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-3">
          <h3 className="text-small font-semibold text-onyx">Change package owner</h3>
          <p className="text-caption text-gray-mid">
            Use when this submission was attached to the wrong record. The
            file destination picker below will refresh once the owner changes.
          </p>
          <OwnerPicker value={retargetOwner} onChange={setRetargetOwner} />
          <div className="flex items-center justify-end">
            <Button
              type="button"
              disabled={retargetMut.isPending || retargetOwner === null}
              onClick={() => retargetMut.mutate()}
            >
              Retarget owner
            </Button>
          </div>
        </section>
      )}

      <div className="flex items-center gap-space-2">
        <ScanBadge
          status={submission.scanStatus}
          overriddenAt={submission.scanOverriddenAt}
          overriddenBy={submission.scanOverriddenById}
          overrideReason={submission.scanOverrideReason}
          tz={tz}
        />
        {downloadUrl.data && (
          <a
            href={downloadUrl.data.url}
            target="_blank"
            rel="noreferrer"
            onClick={markOpened}
            className="text-small text-onyx hover:text-gold-shadow"
          >
            Open
          </a>
        )}
      </div>
      <DocumentComparisonPanel
        left={{
          title: "Submitted file",
          fileName: submission.originalFileName,
          contentType: submission.contentType,
          // Inline-preview an Office source via its converted PDF (previewUrl),
          // but keep Download/Open on the original file (url). Withhold the
          // inline preview (url:null) until the malware scan clears so a payload
          // can't auto-execute on render; Download stays available below.
          url: scanPreviewAllowed
            ? downloadUrl.data?.previewUrl ?? downloadUrl.data?.url ?? null
            : null,
          isPdf: downloadUrl.data?.isPdf,
          state: !scanPreviewAllowed
            ? submission.scanStatus === "Pending"
              ? "scanning"
              : "error"
            : downloadUrl.isLoading
              ? "loading"
              : downloadUrl.data?.url
                ? "ready"
                : "unavailable",
          message: !scanPreviewAllowed
            ? scanPreviewBlockedReason
            : downloadUrl.isError
              ? "Could not prepare the source preview."
              : undefined,
          onDownload: downloadUrl.data?.url
            ? () => {
                markOpened();
                window.open(downloadUrl.data.url, "_blank", "noopener,noreferrer");
              }
            : undefined,
        }}
        right={{
          title: "AI distillation document",
          fileName: "AI assessment.pdf",
          contentType: "application/pdf",
          url: assessmentPreviewUrl.data?.url ?? null,
          state: submission.assessmentStatus === "Pending"
            ? "generating"
            : submission.assessmentStatus === "Failed"
              ? "failed"
              : assessmentPreviewUrl.isLoading
                ? "loading"
                : assessmentPreviewUrl.data?.url
                  ? "ready"
                  : "unavailable",
          message: submission.assessmentStatus === "Skipped"
            ? "This template has no assessment prompt configured."
            : submission.assessmentStatus === "Failed"
              ? submission.assessmentError ??
                "AI assessment generation failed. The markdown fallback is shown when available."
              : undefined,
          generatingSince: submission.assessmentStartedAt,
          // Regenerate is a recovery action for a stuck/failed assessment —
          // hide it while a generation is still in flight (assessmentStatus
          // "Pending") so the reviewer isn't offered a premature re-run.
          onRegenerate:
            canReview &&
            submission.reviewStatus === "AwaitingReview" &&
            submission.assessmentStatus !== "Pending"
              ? () => regenerateMut.mutate()
              : undefined,
          regenerateDisabled: regenerateMut.isPending,
          markdownFallback: submission.aiAssessmentMarkdown ? (
            <MarkdownReader markdown={submission.aiAssessmentMarkdown} className="text-small" />
          ) : undefined,
          onDownload: assessmentPreviewUrl.data?.url
            ? () => window.open(assessmentPreviewUrl.data.url, "_blank", "noopener,noreferrer")
            : undefined,
        }}
        onLeftOpened={markOpened}
      />

      {submission.templateInstructions && (
        <section className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-3">
          <h3 className="text-small font-semibold text-onyx">Submission Instructions</h3>
          <MarkdownReader markdown={submission.templateInstructions} className="text-small" />
        </section>
      )}

      {submission.isBrokerApplicationPersonalInformation && reviewFormSchema && (
        <section className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-3 bg-white-pure">
          <div className="flex items-start justify-between gap-space-3">
            <div>
              <h3 className="text-small font-semibold text-onyx">
                Broker Application — Personal Information answers
              </h3>
              <p className="text-caption text-gray-mid">
                Review and correct these answers before importing. Import saves any unsaved edits first.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={!reviewFormDirty || saveFormData.isPending || !answersEditable}
              loading={saveFormData.isPending}
              onClick={() => saveFormData.mutate()}
            >
              Save reviewed answers
            </Button>
          </div>
          {importBlockReasons.length > 0 && (
            <div className="rounded-md border border-status-error bg-status-error/5 p-space-3 flex flex-col gap-space-2">
              <div className="flex items-center gap-space-2 text-small font-semibold text-status-error">
                <AlertTriangle size={16} />
                Import is blocked — fix the field{importBlockReasons.length === 1 ? "" : "s"} below, then import again.
              </div>
              <ul className="m-0 flex list-none flex-col gap-space-1 p-0">
                {importBlockReasons.map((r, idx) => (
                  <li key={idx} className="text-small text-status-error">
                    {r.label ? <strong>{r.label}: </strong> : null}
                    {r.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <PortalFormRenderer
            schema={reviewFormSchema}
            value={reviewFormData}
            disabled={!answersEditable || saveFormData.isPending || importBrokerApplication.isPending}
            onChange={(next) => {
              setReviewFormData(next);
              setReviewFormDirty(true);
            }}
          />
          <p className="text-caption text-gray-mid">
            File attachment import is not supported yet; file fields can show captured names but do not attach a durable broker document.
          </p>
        </section>
      )}

      {submission.reviewStatus === "AwaitingReview" && (
        <ScanRecoveryControls
          scanStatus={submission.scanStatus}
          scanError={submission.scanError}
          canRefresh={canReview}
          onRefresh={() => refreshScanMut.mutate()}
          refreshing={refreshScanMut.isPending}
        />
      )}

      {canOverrideScan &&
        submission.reviewStatus === "AwaitingReview" &&
        (submission.scanStatus === "Pending" || submission.scanStatus === "Error") &&
        (overrideMode ? (
          <div className="rounded border border-amber-300 bg-amber-50 p-space-3 flex flex-col gap-space-2">
            <span className="text-small text-onyx">
              Mark this submission as scanned so it can be filed. The reason is recorded in audit.
            </span>
            <Input
              type="text"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="e.g. Defender not wired on this environment; visually inspected."
            />
            <div className="flex items-center justify-end gap-space-2">
              <Button type="button" variant="secondary" onClick={() => { setOverrideMode(false); setOverrideReason(""); }}>
                Cancel
              </Button>
              <Button type="button" disabled={overrideReason.trim().length === 0 || overrideMut.isPending} onClick={() => overrideMut.mutate()}>
                Override scan
              </Button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setOverrideMode(true)} className="self-start text-caption text-onyx underline hover:text-gold-shadow">
            Scan pending — override available
          </button>
        ))}

      {/* Review loop — one chronological Review history (newest first)
          spanning every submission for this item, so the reviewer sees the
          whole conversation after a resubmission supersedes the prior row.
          Names only: staff resolved from their ids, recipients by name. */}
      {reviewEvents.length > 0 && (
        <section
          className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-2"
          data-testid="review-history"
        >
          <h3 className="text-small font-semibold text-onyx">Review history</h3>
          <ol className="m-0 flex list-none flex-col gap-space-2 p-0">
            {reviewEvents.map((e) => (
              <li
                key={e.id}
                className="rounded bg-paper-warm p-space-3 text-small text-onyx flex flex-wrap items-start justify-between gap-space-2"
              >
                <p className="m-0 whitespace-pre-wrap">
                  {reviewEventText(e, reviewEventActor(e, staffName))}
                </p>
                <span className="text-caption text-gray-mid whitespace-nowrap">
                  {dateTime(e.createdAt)}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* File-into picker — quick-pick chips (package owner + the recipient's
          attached records) re-scope the shared paginated picker below, which
          server-pages the "Search every folder by name…" results (25 + See
          more) and renders the active anchor's folder tree. The backend
          re-validates folder permissions on approve. */}
      <section className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-3">
        <header className="flex items-center justify-between">
          <h3 className="text-small font-semibold text-onyx">File into folder</h3>
          {suggestion.data?.suggestedFolderName && (
            <span className="text-caption text-gray-mid">
              Suggested: <strong>{suggestion.data.suggestedFolderName}</strong>{" "}
              ({suggestion.data.reason})
            </span>
          )}
        </header>

        <DestinationQuickPicks
          options={destinations.data ?? []}
          isLoading={destinations.isLoading}
          activeAnchor={anchor}
          onPick={(option) => {
            setAnchor({
              entityType: option.entityType,
              entityId: option.entityId,
            });
            if (option.folderId) setFolderId(option.folderId);
          }}
        />

        <FolderPicker
          value={folderId}
          onChange={(id, folder) => {
            setFolderId(id);
            // A cross-entity search pick re-anchors the inline tree to that
            // folder's owner so its subfolders become browsable (same-owner
            // tree picks re-anchor to the current owner — a no-op).
            if (folder?.entityType && folder?.entityId) {
              setAnchor({ entityType: folder.entityType, entityId: folder.entityId });
            }
          }}
          treeSource={
            anchor
              ? { kind: "entity", entityType: anchor.entityType, entityId: anchor.entityId }
              : null
          }
          search={{ placeholder: "Search every folder by name…" }}
          browseByDefault
        />
      </section>

      {/* Actions */}
      {!returnMode ? (
        <div className="flex flex-col gap-space-3">
          {/* Auto-link toggle — present only when a filed AI distillation
              exists. Default on; toggling off skips the AiAnalysisOf link. */}
          {hasAiDistillation && (
            <div className="flex items-start gap-3 p-space-3 bg-white-warm border border-onyx/8 rounded">
              <button
                type="button"
                role="switch"
                aria-checked={linkAiDistillation}
                aria-label="Link AI distillation to source document"
                onClick={() => setLinkAiDistillation((v) => !v)}
                className="mt-[2px] shrink-0"
              >
                <span
                  className={`relative inline-block w-[32px] h-[18px] rounded-full transition-colors ${
                    linkAiDistillation ? "bg-gold" : "bg-border-light"
                  }`}
                  aria-hidden
                >
                  <span
                    className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-white-pure shadow transition-transform ${
                      linkAiDistillation ? "translate-x-[14px]" : ""
                    }`}
                  />
                </span>
              </button>
              <div className="text-small">
                <div className="font-medium">Link AI distillation to source</div>
                <div className="text-onyx/60">
                  When on, the AI distillation will be filed AND linked to the
                  source document. Toggle off if you don&apos;t want these two
                  paired (rare).
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-end gap-space-3">
          {/* Review loop — why an action is unavailable, said in words beside
              the buttons (the Returned banner says it for a Returned row). */}
          {actionReasons.length > 0 && (
            <ul
              className="m-0 mr-auto flex list-none flex-col gap-space-1 p-0"
              data-testid="review-action-reasons"
            >
              {actionReasons.map((reason) => (
                <li key={reason} className="text-caption text-gray-mid">
                  {reason}
                </li>
              ))}
            </ul>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => setReturnMode(true)}
            disabled={!isAwaiting || !fileOpened}
            aria-describedby={showReturnedBanner ? returnedBannerId : undefined}
          >
            <RotateCcw size={14} className="mr-1 inline" />
            Return with comments
          </Button>
          {submission.isBrokerApplicationPersonalInformation && (
            <Button
              type="button"
              variant="secondary"
              disabled={!submission.brokerApplicationTargetBrokerId || importBrokerApplication.isPending}
              loading={importBrokerApplication.isPending}
              onClick={() => importBrokerApplication.mutate()}
              title={submission.brokerApplicationTargetBrokerId ? undefined : "Import is unavailable until this submission is linked to a Broker record."}
            >
              <Download size={14} className="mr-1 inline" />
              Import
            </Button>
          )}
          {/* Once done, Approve / File read as DONE — a status, not a live
              control that does nothing when pressed. */}
          {isApproved ? (
            <Badge status="finalized" data-testid="review-done-approved">
              <CheckCircle2 size={12} aria-hidden /> Approved
            </Badge>
          ) : (
            <Button
              type="button"
              disabled={!canApprove || approve.isPending}
              loading={approve.isPending}
              onClick={() => approve.mutate()}
              aria-describedby={showReturnedBanner ? returnedBannerId : undefined}
            >
              <CheckCircle2 size={14} className="mr-1 inline" />
              Approve
            </Button>
          )}
          {isFiled ? (
            <Badge status="finalized" data-testid="review-done-filed">
              <Archive size={12} aria-hidden /> Filed
            </Badge>
          ) : (
            <Button
              type="button"
              disabled={!canFile || file.isPending}
              loading={file.isPending}
              onClick={() => file.mutate()}
              aria-describedby={showReturnedBanner ? returnedBannerId : undefined}
            >
              <Archive size={14} className="mr-1 inline" />
              File
            </Button>
          )}
          </div>
        </div>
      ) : (
        <section className="rounded-md border border-gray-light p-space-4 flex flex-col gap-space-3">
          <h3 className="text-small font-semibold text-onyx">
            Return with comments
          </h3>
          <FormField label="Comment to the recipient" required>
            <Input
              type="text"
              value={returnNote}
              onChange={(e) => setReturnNote(e.target.value)}
              placeholder="Describe what needs to change so the recipient can re-upload."
            />
          </FormField>
          <div className="flex items-center justify-end gap-space-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setReturnMode(false);
                setReturnNote("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                returnNote.trim().length === 0 || returnMut.isPending
              }
              onClick={() => returnMut.mutate()}
            >
              Send return
            </Button>
          </div>
        </section>
      )}

      {/* PROB-C — confirm before sending an on-demand reminder email. */}
      <ConfirmModal
        open={remindConfirmOpen}
        title="Send reminder?"
        message={`A reminder email will be sent to ${submission.recipientName} (${submission.recipientEmail}) for this collection package.`}
        confirmLabel="Send reminder"
        onConfirm={() => {
          setRemindConfirmOpen(false);
          remindMut.mutate();
        }}
        onCancel={() => setRemindConfirmOpen(false)}
      />

      {/* PROB-E — in-app archive confirmation (replaces window.confirm). */}
      <ConfirmModal
        open={archiveConfirmOpen}
        title="Archive this package?"
        message="It will be hidden from active queues but stays in audit history. You can restore it later from the Archived view."
        confirmLabel="Archive"
        destructive
        onConfirm={() => {
          setArchiveConfirmOpen(false);
          archiveMut.mutate();
        }}
        onCancel={() => setArchiveConfirmOpen(false)}
      />
    </div>
  );
}

// Prompt 9-iii — quick-pick chips. The first entry is always the package
// owner; the rest are records the recipient's external account is
// attached to. Clicking a chip switches the inline folder tree's anchor
// and (when an entity root is already provisioned) pre-selects it.
function DestinationQuickPicks({
  options,
  isLoading,
  activeAnchor,
  onPick,
}: {
  options: StagedDestinationOption[];
  isLoading: boolean;
  activeAnchor: { entityType: string; entityId: string } | null;
  onPick: (option: StagedDestinationOption) => void;
}) {
  if (isLoading && options.length === 0) {
    return (
      <p className="text-caption text-gray-mid">Loading attached records…</p>
    );
  }
  if (options.length === 0) return null;
  return (
    <div className="flex flex-col gap-space-2">
      <span className="text-caption text-gray-mid">
        File under (the recipient's account is attached to):
      </span>
      <div className="flex flex-wrap gap-space-2">
        {options.map((opt) => {
          const isActive =
            activeAnchor != null &&
            activeAnchor.entityType.toLowerCase() ===
              opt.entityType.toLowerCase() &&
            activeAnchor.entityId === opt.entityId;
          return (
            <button
              key={`${opt.entityType}:${opt.entityId}`}
              type="button"
              onClick={() => onPick(opt)}
              title={opt.note ?? undefined}
              className={[
                "rounded-full border px-space-3 py-space-1 text-caption transition",
                isActive
                  ? "border-gold-shadow bg-paper-warm text-onyx"
                  : "border-gray-light text-onyx hover:border-gold-shadow",
              ].join(" ")}
            >
              <span className="font-semibold">{opt.displayLabel}</span>
              <span className="ml-space-1 text-gray-mid">
                · {opt.source === "Owner" ? "package owner" : opt.entityType}
              </span>
              {opt.note && (
                <span className="ml-space-1 text-gray-mid italic">
                  — {opt.note}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

